// src/components/VariantCreator.tsx
// UPDATED: Enhanced validation and error handling for empty piece arrays
// UPDATED: Now uses PageHeader component for consistent header format
// UPDATED: Uses VariantProfiles component for save/load/import/export
import React, { useState, useEffect } from "react";
import { Position, Piece, PieceClass } from "../types/Board.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";
import { PIECE_VALUES } from "../data/pieceValues.ts";
import { useAuth } from "../context/AuthContext.tsx";
import { useCustomVariants, CustomVariant } from "../hooks/useCustomVariants.ts";
import PageHeader from "./PageHeader.tsx";
import VariantProfiles from "./VariantProfiles.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";
import "./VariantCreator.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const MAX_CUSTOM_VARIANTS = 12;

interface VariantConfig {
  id?: string;
  name: string;
  description: string;
  boardSize: number;
  rowsOccupied: number;
  playersPerSide: number;
  movesPerTurn: number;
  checkmatepiece: string;
  whitePieces: PlacedPiece[];
  blackPieces: PlacedPiece[];
  specialRules: {
    castling: boolean;
    enPassant: boolean;
    slideBishop: boolean;
  };
}

interface PlacedPiece {
  type: string;
  abbreviation: string;
  position: Position;
  class: PieceClass;
  index: [number, number];
  color: "white" | "black";
}

interface ValidationIssue {
  type: "error" | "warning";
  message: string;
}

const VariantCreator: React.FC = () => {
  const { user } = useAuth();
  const { customVariants, refreshVariants } = useCustomVariants();
  const [boardSize, setBoardSize] = useState<number>(8);
  const [pieces, setPieces] = useState<Map<string, PlacedPiece>>(new Map());
  const [selectedPieceType, setSelectedPieceType] = useState<string | null>(
    null
  );
  const [selectedColor, setSelectedColor] = useState<"white" | "black">(
    "white"
  );
  const [symmetryMode, setSymmetryMode] = useState<boolean>(true);
  const [variantName, setVariantName] = useState<string>("My Custom Variant");
  const [variantDescription, setVariantDescription] = useState<string>("");
  const [lightSquareColor, setLightSquareColor] = useState<string>("#f0d9b5");
  const [darkSquareColor, setDarkSquareColor] = useState<string>("#b58863");
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>(
    []
  );
  const [levelFilters, setLevelFilters] = useState<Record<string, boolean>>({
    L: true,
    I: false,
    H: false,
    C: false,
  });
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showClearBoardModal, setShowClearBoardModal] = useState<boolean>(false);
  const [showPlacementRulesModal, setShowPlacementRulesModal] = useState<boolean>(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState<boolean>(false);
  const [variantCount, setVariantCount] = useState<number>(0);
  const [levelMismatchError, setLevelMismatchError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loadedVariantId, setLoadedVariantId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Guidelines based on board size
  const getGuidelines = (size: number) => {
    const guidelines: Record<number, any> = {
      8: {
        rows: 2,
        pieces: 16,
        addPieces: 0,
        recommendations: [
          "Maximum 2 rows occupied at start",
          "Non-symetrical piece placement",
          "Use King as checkmate piece",
          "Castling and En Passant enabled",
          "Slide Bishop, Preist & Nun not enabled",
          "Use Castle at back corners",
          "Use Pawns on front row",
          "Consider replacing Knights, Bishops, Queens with new pieces",
        ],
      },
      9: {
        rows: 2,
        pieces: 18,
        addPieces: 2,
        recommendations: [
          "Maximum 2 rows occupied at start",
          "Use symetrical piece placement",
          "Use King as checkmate piece",
          "Castling and En Passant enabled",
          "Slide Bishop, Preist & Nun enabled",
          "Use Castle at back corners",
          "Use Pawns on front row",
          "Consider replacing Knights, Bishops, Queens with new pieces",
        ],
      },
      10: {
        rows: 3,
        pieces: 20,
        addPieces: 4,
        recommendations: [
          "Maximum 2 rows occupied at start",
          "Non-symetrical piece placement",
          "Consider Emperor instead of King as checkmate piece",
          "Castling and En Passant not enabled",
          "Slide Bishop, Preist & Nun not enabled",
          "Consider Fortress at back corners",
          "Use Knaves on front row",
          "Consider replacing Knights, Bishops, Queens with new pieces",
        ],
      },
      11: {
        rows: 3,
        pieces: 33,
        addPieces: 17,
        recommendations: [
          "Maximum 3 rows occupied at start",
          "Use symetrical piece placement",
          "Use Emperor as checkmate piece",
          "Castling and En Passant not enabled",
          "Slide Bishop, Preist & Nun enabled",
          "Use Fortress at back corners",
          "Use Knaves on front row",
          "Use mix of low and intermediate level pieces",
        ],
      },
      12: {
        rows: 3,
        pieces: 36,
        addPieces: 20,
        recommendations: [
          "Maximum 3 rows occupied at start",
          "Non-symetrical piece placement",
          "Use Emperor as checkmate piece",
          "Castling and En Passant not enabled",
          "Slide Bishop, Preist & Nun enabled",
          "Use Fortress at back corners",
          "Use Knaves on front row",
          "Use mix of low and intermediate level pieces",
        ],
      },
    };
    return guidelines[size] || guidelines[8];
  };

  const guidelines = getGuidelines(boardSize);

  // Fetch variant count when user logs in
  useEffect(() => {
    if (user) {
      // Debug: Log user object structure
      console.log("👤 User object structure:", {
        fullUser: user,
        hasId: !!(user as any).id,
        hasUserId: !!(user as any).userId,
        hasUser_id: !!(user as any).user_id,
        username: (user as any).username,
      });
      fetchVariantCount();
    }
  }, [user]);

  const fetchVariantCount = async () => {
    try {
      if (!user) return;

      // Handle different user ID property names
      const userId =
        (user as any).user_id || (user as any).id || (user as any).userId;
      if (!userId) {
        console.error(
          "Cannot fetch variant count: user ID not found in user object"
        );
        return;
      }

      const response = await fetch(
        `${API_URL}/api/custom-variants/${userId}/count`
      );
      if (response.ok) {
        const data = await response.json();
        setVariantCount(data.count);
      }
    } catch (error) {
      console.error("Error fetching variant count:", error);
    }
  };

  // Calculate total piece values for white and black using useMemo for proper reactivity
  const { whiteValue, blackValue } = React.useMemo(() => {
    let whiteValue = 0;
    let blackValue = 0;
    const midPoint = Math.floor(boardSize / 2);

    pieces.forEach((piece) => {
      const pieceValuesArray = PIECE_VALUES[piece.abbreviation];

      if (pieceValuesArray && pieceValuesArray.length > 0) {
        const boardSizeKey = `${boardSize}×${boardSize}`;
        const valueEntry = pieceValuesArray.find(
          (v) => v.boardSize === boardSizeKey
        );
        const pieceValue = valueEntry ? valueEntry.value : 0;

        if (piece.position.row < midPoint) {
          blackValue += pieceValue;
        } else {
          whiteValue += pieceValue;
        }
      }
    });

    return {
      whiteValue: Math.round(whiteValue * 100) / 100,
      blackValue: Math.round(blackValue * 100) / 100,
    };
  }, [pieces, boardSize]);

  // Get all pieces organized by row (movement type)
  const piecesByRow = {
    row6: Object.values(PIECE_DEFINITIONS).filter((p) => p.index[0] === 6),
    row5: Object.values(PIECE_DEFINITIONS).filter((p) => p.index[0] === 5),
    row4: Object.values(PIECE_DEFINITIONS).filter((p) => p.index[0] === 4),
    row3: Object.values(PIECE_DEFINITIONS).filter((p) => p.index[0] === 3),
    row2: Object.values(PIECE_DEFINITIONS).filter((p) => p.index[0] === 2),
    row1: Object.values(PIECE_DEFINITIONS).filter((p) => p.index[0] === 1),
  };

  // Piece level definitions based on validation rules
  type PieceLevel = 'L' | 'I' | 'H' | 'C';

  const getPieceLevel = (abbreviation: string): PieceLevel => {
    const levelMap: Record<string, PieceLevel> = {
      // Low (L) - Forward moving, limited range
      'PWN': 'L', 'KNV': 'L', 'SQR': 'L',
      // Intermediate (I) - Limited range in all directions
      'DUK': 'I', 'CHL': 'I', 'CDL': 'I', 'CTL': 'I', 'ARM': 'I', 'ABY': 'I',
      'ARB': 'I', 'CRD': 'I', 'PRS': 'I', 'NUN': 'I', 'MSL': 'I', 'CPN': 'I',
      'SGT': 'I', 'GRD': 'I', 'ARC': 'I', 'KNT': 'I', 'RAM': 'I', 'MAC': 'I',
      'LNC': 'I', 'HCT': 'I', 'SWD': 'I', 'SNT': 'I', 'WZD': 'I',
      // High (H) - Unlimited range in multiple directions
      'EPS': 'H', 'QUN': 'H', 'FTS': 'H', 'CSL': 'H', 'POP': 'H', 'BSP': 'H', 'CTP': 'H', 'AGL': 'H',
      // Checkmate (C) - Game-ending pieces
      'KNG': 'C', 'EPR': 'C',
    };
    return levelMap[abbreviation] || 'I';
  };

  const getLevelColor = (level: PieceLevel): string => {
    const colors: Record<PieceLevel, string> = {
      'L': '#607D8B',
      'I': '#2196F3',
      'H': '#9C27B0',
      'C': '#F44336',
    };
    return colors[level];
  };

  // Load a saved variant into the editor
  const loadVariantIntoEditor = (variant: CustomVariant) => {
    // Track the loaded variant for edit mode
    setLoadedVariantId(variant.id || null);
    setIsEditMode(false); // Reset edit mode when loading

    // Update board size first
    setBoardSize(variant.boardSize);
    setVariantName(variant.name);
    setVariantDescription("");

    // Build pieces map from white and black pieces
    const newPieces = new Map<string, PlacedPiece>();

    variant.whitePieces?.forEach((piece: any) => {
      const key = `${piece.position.row}-${piece.position.col}`;
      const definition = PIECE_DEFINITIONS[piece.type?.toLowerCase()];
      newPieces.set(key, {
        type: piece.type,
        abbreviation: definition?.abbreviation || piece.abbreviation || piece.type?.substring(0, 3).toUpperCase(),
        position: piece.position,
        class: piece.class || definition?.class || "low",
        index: piece.index || definition?.index || [0, 0],
        color: "white",
      });
    });

    variant.blackPieces?.forEach((piece: any) => {
      const key = `${piece.position.row}-${piece.position.col}`;
      const definition = PIECE_DEFINITIONS[piece.type?.toLowerCase()];
      newPieces.set(key, {
        type: piece.type,
        abbreviation: definition?.abbreviation || piece.abbreviation || piece.type?.substring(0, 3).toUpperCase(),
        position: piece.position,
        class: piece.class || definition?.class || "low",
        index: piece.index || definition?.index || [0, 0],
        color: "black",
      });
    });

    setPieces(newPieces);
  };

  // Get current variant data for VariantProfiles component
  const getCurrentVariantData = () => {
    const whitePieces: PlacedPiece[] = [];
    const blackPieces: PlacedPiece[] = [];
    const midPoint = Math.floor(boardSize / 2);

    pieces.forEach((piece) => {
      if (piece.position.row < midPoint) {
        blackPieces.push(piece);
      } else {
        whitePieces.push(piece);
      }
    });

    // Get checkmate piece
    const whiteKingOrEmperor = whitePieces.find(
      (p) => p.type === "king" || p.type === "emperor"
    );

    return {
      name: variantName,
      description: variantDescription,
      boardSize: boardSize,
      whitePieces: whitePieces,
      blackPieces: blackPieces,
      specialRules: {
        castling: boardSize <= 10,
        enPassant: boardSize <= 10,
        slideBishop: boardSize % 2 !== 0,
      },
    };
  };

  // Check if current configuration is valid for saving
  const hasValidConfiguration = (): boolean => {
    const errors = validationIssues.filter((issue) => issue.type === "error");
    if (errors.length > 0) return false;

    const midPoint = Math.floor(boardSize / 2);
    let whiteCount = 0;
    let blackCount = 0;

    pieces.forEach((piece) => {
      if (piece.position.row < midPoint) {
        blackCount++;
      } else {
        whiteCount++;
      }
    });

    return whiteCount > 0 && blackCount > 0;
  };

  const getLevelName = (level: PieceLevel): string => {
    const names: Record<PieceLevel, string> = {
      'L': 'Low',
      'I': 'Intermediate',
      'H': 'High',
      'C': 'Checkmate',
    };
    return names[level];
  };

  const piecesToDisplay = Object.values(PIECE_DEFINITIONS).filter((p) => {
    const pieceLevel = getPieceLevel(p.abbreviation);

    // For 8x8 and 9x9 boards, only show pawns for L level filter
    if ((boardSize === 8 || boardSize === 9) && pieceLevel === 'L') {
      // Only show pawn (PWN) for L level on 8x8 and 9x9 boards
      return levelFilters['L'] && p.abbreviation === 'PWN';
    }

    return levelFilters[pieceLevel];
  });

  const positionKey = (pos: Position) => `${pos.row}-${pos.col}`;

  const getPieceAt = (row: number, col: number): PlacedPiece | undefined => {
    return pieces.get(positionKey({ row, col }));
  };

  const handleSquareClick = (row: number, col: number) => {
    if (!selectedPieceType) return;

    const key = positionKey({ row, col });
    const existingPiece = pieces.get(key);

    // Clear any previous error when clicking
    setLevelMismatchError(null);

    if (existingPiece) {
      const newPieces = new Map(pieces);
      newPieces.delete(key);
      setPieces(newPieces);
      return;
    }

    const definition = PIECE_DEFINITIONS[selectedPieceType];
    if (!definition) return;

    // Check level compatibility
    const squareLevelBadge = getSquareLevelBadge(row, col, boardSize);
    const pieceLevel = getPieceLevel(definition.abbreviation);

    // If square has no level badge, don't allow placement
    if (!squareLevelBadge) {
      setLevelMismatchError("Placement not permitted on this square (no level assigned)");
      setTimeout(() => setLevelMismatchError(null), 5000);
      return;
    }

    const squareLevel = squareLevelBadge.level;
    // Handle combined levels like 'I/H' - piece level must be one of them
    const allowedLevels = squareLevel.split('/');
    if (!allowedLevels.includes(pieceLevel)) {
      setLevelMismatchError(`Placement not permitted, level mismatch: Piece(${pieceLevel}) / Square(${squareLevel})`);
      // Auto-clear the error after 5 seconds
      setTimeout(() => setLevelMismatchError(null), 5000);
      return;
    }

    const newPiece: PlacedPiece = {
      type: definition.type,
      abbreviation: definition.abbreviation,
      position: { row, col },
      class: definition.class,
      index: definition.index,
      color: selectedColor,
    };

    const newPieces = new Map(pieces);
    newPieces.set(key, newPiece);

    if (symmetryMode && selectedColor === "white") {
      const mirrorRow = boardSize - 1 - row;
      const mirrorKey = positionKey({ row: mirrorRow, col });

      // Also check level for mirror square
      const mirrorSquareLevelBadge = getSquareLevelBadge(mirrorRow, col, boardSize);
      if (!mirrorSquareLevelBadge) {
        setLevelMismatchError("Placement not permitted on mirror square (no level assigned)");
        setTimeout(() => setLevelMismatchError(null), 5000);
        return;
      }

      const mirrorSquareLevel = mirrorSquareLevelBadge.level;
      const mirrorAllowedLevels = mirrorSquareLevel.split('/');
      if (!mirrorAllowedLevels.includes(pieceLevel)) {
        setLevelMismatchError(`Placement not permitted, level mismatch on mirror square: Piece(${pieceLevel}) / Square(${mirrorSquareLevel})`);
        setTimeout(() => setLevelMismatchError(null), 5000);
        return;
      }

      const mirrorPiece: PlacedPiece = {
        ...newPiece,
        position: { row: mirrorRow, col },
        color: "black",
      };
      newPieces.set(mirrorKey, mirrorPiece);
    }

    setPieces(newPieces);
  };

  const clearBoard = () => {
    if (pieces.size > 0) {
      setShowClearBoardModal(true);
      return;
    }
    setPieces(new Map());
    setLevelFilters({ L: true, I: false, H: false, C: false });
    setSelectedPieceType(null);
  };

  const confirmClearBoard = () => {
    setPieces(new Map());
    setShowClearBoardModal(false);
    // Reset edit mode when clearing board
    setLoadedVariantId(null);
    setIsEditMode(false);
    // Deselect any selected piece to restart flashing on next edit
    setSelectedPieceType(null);
    // Reset level to L
    setLevelFilters({ L: true, I: false, H: false, C: false });
  };

  // Get the level badge(s) for a square based on board size and position
  const getSquareLevelBadge = (row: number, col: number, boardSize: number): { level: string; color: string } | null => {
    if (boardSize === 8) {
      // White back rank (row 7)
      if (row === 7) {
        if (col === 0 || col === 7) return { level: 'H', color: '#9C27B0' }; // corners
        if (col === 1 || col === 6) return { level: 'I', color: '#2196F3' }; // knight squares
        if (col === 2 || col === 5) return { level: 'H', color: '#9C27B0' }; // bishop squares
        if (col === 3) return { level: 'H', color: '#9C27B0' }; // queen square
        if (col === 4) return { level: 'C', color: '#F44336' }; // king square
      }
      // Black back rank (row 0)
      if (row === 0) {
        if (col === 0 || col === 7) return { level: 'H', color: '#9C27B0' };
        if (col === 1 || col === 6) return { level: 'I', color: '#2196F3' };
        if (col === 2 || col === 5) return { level: 'H', color: '#9C27B0' };
        if (col === 3) return { level: 'H', color: '#9C27B0' };
        if (col === 4) return { level: 'C', color: '#F44336' };
      }
      // White pawn row (row 6)
      if (row === 6) return { level: 'L', color: '#607D8B' };
      // Black pawn row (row 1)
      if (row === 1) return { level: 'L', color: '#607D8B' };
    }

    if (boardSize === 9) {
      // White back rank (row 8)
      if (row === 8) {
        if (col === 0 || col === 8) return { level: 'H', color: '#9C27B0' }; // corners
        if (col === 1 || col === 7) return { level: 'I', color: '#2196F3' }; // knight squares
        if (col === 2 || col === 6) return { level: 'I/H', color: '#7B1FA2' }; // I + H combined
        if (col === 3 || col === 5) return { level: 'H', color: '#9C27B0' }; // bishop-like squares
        if (col === 4) return { level: 'C', color: '#F44336' }; // king square
      }
      // Black back rank (row 0)
      if (row === 0) {
        if (col === 0 || col === 8) return { level: 'H', color: '#9C27B0' };
        if (col === 1 || col === 7) return { level: 'I', color: '#2196F3' };
        if (col === 2 || col === 6) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 3 || col === 5) return { level: 'H', color: '#9C27B0' };
        if (col === 4) return { level: 'C', color: '#F44336' };
      }
      // White pawn row (row 7)
      if (row === 7) return { level: 'L', color: '#607D8B' };
      // Black pawn row (row 1)
      if (row === 1) return { level: 'L', color: '#607D8B' };
    }

    // 10x10 Board (2 rows per side)
    if (boardSize === 10) {
      // White back rank (row 9)
      if (row === 9) {
        if (col === 0 || col === 9) return { level: 'H', color: '#9C27B0' }; // a1/j1 - corners
        if (col === 1 || col === 8) return { level: 'I', color: '#2196F3' }; // b1/i1
        if (col === 2 || col === 7) return { level: 'I/H', color: '#7B1FA2' }; // c1/h1
        if (col === 3 || col === 6) return { level: 'H', color: '#9C27B0' }; // d1/g1
        if (col === 4) return { level: 'C', color: '#F44336' }; // e1 - checkmate
        if (col === 5) return { level: 'H', color: '#9C27B0' }; // f1
      }
      // Black back rank (row 0)
      if (row === 0) {
        if (col === 0 || col === 9) return { level: 'H', color: '#9C27B0' };
        if (col === 1 || col === 8) return { level: 'I', color: '#2196F3' };
        if (col === 2 || col === 7) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 3 || col === 6) return { level: 'H', color: '#9C27B0' };
        if (col === 4) return { level: 'C', color: '#F44336' };
        if (col === 5) return { level: 'H', color: '#9C27B0' };
      }
      // White pawn row (row 8)
      if (row === 8) return { level: 'L', color: '#607D8B' };
      // Black pawn row (row 1)
      if (row === 1) return { level: 'L', color: '#607D8B' };
    }

    // 11x11 Board (3 rows per side)
    if (boardSize === 11) {
      // White back rank 1 (row 10)
      if (row === 10) {
        if (col === 0 || col === 10) return { level: 'H', color: '#9C27B0' }; // a1/k1 - corners
        if (col === 1 || col === 9) return { level: 'I/H', color: '#7B1FA2' }; // b1/j1
        if (col === 2 || col === 8) return { level: 'I/H', color: '#7B1FA2' }; // c1/i1
        if (col === 3 || col === 7) return { level: 'H', color: '#9C27B0' }; // d1/h1
        if (col === 4 || col === 6) return { level: 'H', color: '#9C27B0' }; // e1/g1
        if (col === 5) return { level: 'C', color: '#F44336' }; // f1 - checkmate
      }
      // White row 2 (row 9)
      if (row === 9) {
        if (col === 0 || col === 1 || col === 2) return { level: 'I', color: '#2196F3' }; // a2/b2/c2
        if (col === 3 || col === 7) return { level: 'I/H', color: '#7B1FA2' }; // d2/h2
        if (col === 4 || col === 6) return { level: 'I/H', color: '#7B1FA2' }; // e2/g2
        if (col === 5) return { level: 'H', color: '#9C27B0' }; // f2
        if (col === 8 || col === 9 || col === 10) return { level: 'I', color: '#2196F3' }; // i2/j2/k2
      }
      // White pawn row (row 8)
      if (row === 8) return { level: 'L', color: '#607D8B' };
      // Black back rank 1 (row 0)
      if (row === 0) {
        if (col === 0 || col === 10) return { level: 'H', color: '#9C27B0' };
        if (col === 1 || col === 9) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 2 || col === 8) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 3 || col === 7) return { level: 'H', color: '#9C27B0' };
        if (col === 4 || col === 6) return { level: 'H', color: '#9C27B0' };
        if (col === 5) return { level: 'C', color: '#F44336' };
      }
      // Black row 2 (row 1)
      if (row === 1) {
        if (col === 0 || col === 1 || col === 2) return { level: 'I', color: '#2196F3' };
        if (col === 3 || col === 7) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 4 || col === 6) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 5) return { level: 'H', color: '#9C27B0' };
        if (col === 8 || col === 9 || col === 10) return { level: 'I', color: '#2196F3' };
      }
      // Black pawn row (row 2)
      if (row === 2) return { level: 'L', color: '#607D8B' };
    }

    // 12x12 Board (3 rows per side)
    if (boardSize === 12) {
      // White back rank 1 (row 11)
      if (row === 11) {
        if (col === 0 || col === 11) return { level: 'H', color: '#9C27B0' }; // a1/l1 - corners
        if (col === 1 || col === 10) return { level: 'I/H', color: '#7B1FA2' }; // b1/k1
        if (col === 2 || col === 9) return { level: 'I/H', color: '#7B1FA2' }; // c1/j1
        if (col === 3 || col === 8) return { level: 'H', color: '#9C27B0' }; // d1/i1
        if (col === 4 || col === 7) return { level: 'H', color: '#9C27B0' }; // e1/h1
        if (col === 5) return { level: 'C', color: '#F44336' }; // f1 - checkmate
        if (col === 6) return { level: 'H', color: '#9C27B0' }; // g1
      }
      // White row 2 (row 10)
      if (row === 10) {
        if (col === 0 || col === 1 || col === 2) return { level: 'I', color: '#2196F3' }; // a2/b2/c2
        if (col === 3 || col === 8) return { level: 'I/H', color: '#7B1FA2' }; // d2/i2
        if (col === 4 || col === 5 || col === 6 || col === 7) return { level: 'H', color: '#9C27B0' }; // e2/f2/g2/h2
        if (col === 9 || col === 10 || col === 11) return { level: 'I', color: '#2196F3' }; // j2/k2/l2
      }
      // White pawn row (row 9)
      if (row === 9) return { level: 'L', color: '#607D8B' };
      // Black back rank 1 (row 0)
      if (row === 0) {
        if (col === 0 || col === 11) return { level: 'H', color: '#9C27B0' };
        if (col === 1 || col === 10) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 2 || col === 9) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 3 || col === 8) return { level: 'H', color: '#9C27B0' };
        if (col === 4 || col === 7) return { level: 'H', color: '#9C27B0' };
        if (col === 5) return { level: 'C', color: '#F44336' };
        if (col === 6) return { level: 'H', color: '#9C27B0' };
      }
      // Black row 2 (row 1)
      if (row === 1) {
        if (col === 0 || col === 1 || col === 2) return { level: 'I', color: '#2196F3' };
        if (col === 3 || col === 8) return { level: 'I/H', color: '#7B1FA2' };
        if (col === 4 || col === 5 || col === 6 || col === 7) return { level: 'H', color: '#9C27B0' };
        if (col === 9 || col === 10 || col === 11) return { level: 'I', color: '#2196F3' };
      }
      // Black pawn row (row 2)
      if (row === 2) return { level: 'L', color: '#607D8B' };
    }

    return null; // No badge for other squares/board sizes
  };

  // Get allowed pieces for a position on 8x8 board based on exact table rules
  const getAllowedPieces8x8 = (row: number, col: number, color: 'white' | 'black'): string[] => {
    // High level pieces (per 8x8 board table - EPS not included)
    const HIGH_PIECES = ['QUN', 'FTS', 'CSL', 'POP', 'BSP', 'CTP', 'AGL'];
    // Intermediate level pieces
    const INTERMEDIATE_PIECES = ['DUK', 'CHL', 'CDL', 'CTL', 'ARM', 'ABY', 'ARB', 'CRD', 'PRS', 'NUN',
      'MSL', 'CPN', 'SGT', 'GRD', 'ARC', 'KNT', 'RAM', 'MAC', 'LNC', 'HCT', 'SWD', 'SNT', 'WZD'];

    // White pieces: back rank row 7, pawn row 6
    // Black pieces: back rank row 0, pawn row 1
    const backRank = color === 'white' ? 7 : 0;
    const pawnRank = color === 'white' ? 6 : 1;

    if (row === backRank) {
      // Back rank positions
      if (col === 0 || col === 7) return HIGH_PIECES; // a1/h1 or a8/h8 - corners (H)
      if (col === 1 || col === 6) return INTERMEDIATE_PIECES; // b1/g1 or b8/g8 - knight squares (I)
      if (col === 2 || col === 5) return HIGH_PIECES; // c1/f1 or c8/f8 - bishop squares (H)
      if (col === 3) return HIGH_PIECES; // d1 or d8 - queen square (H)
      if (col === 4) {
        // e1 or e8 - king square (C)
        return color === 'white' ? ['KNG'] : ['KNG', 'EPR'];
      }
    }
    if (row === pawnRank) {
      return ['PWN']; // Pawn row - only PWN allowed (not KNV, SQR)
    }
    return []; // Other rows - return empty (will be treated as "any piece allowed")
  };

  // Validate piece placement for 8x8 board
  const validatePiecePlacement8x8 = (): ValidationIssue[] => {
    const placementIssues: ValidationIssue[] = [];

    pieces.forEach((piece) => {
      const { row, col } = piece.position;
      const allowedPieces = getAllowedPieces8x8(row, col, piece.color);

      // If allowedPieces is empty, any piece is allowed (middle rows)
      if (allowedPieces.length > 0 && !allowedPieces.includes(piece.abbreviation)) {
        const colLetter = String.fromCharCode(97 + col); // a-h
        const rowNumber = 8 - row; // Convert to chess notation
        const pieceLevel = getPieceLevel(piece.abbreviation);
        const levelNames: Record<PieceLevel, string> = {
          'L': 'Low', 'I': 'Intermediate', 'H': 'High', 'C': 'Checkmate'
        };
        placementIssues.push({
          type: 'error',
          message: `${piece.abbreviation} (${levelNames[pieceLevel]}) not allowed at ${colLetter}${rowNumber}`
        });
      }
    });

    return placementIssues;
  };

  // Get allowed pieces for a position on 9x9 board based on exact table rules
  const getAllowedPieces9x9 = (row: number, col: number, color: 'white' | 'black'): string[] => {
    // High level pieces
    const HIGH_PIECES = ['QUN', 'FTS', 'CSL', 'POP', 'BSP', 'CTP', 'AGL'];
    // Intermediate level pieces
    const INTERMEDIATE_PIECES = ['DUK', 'CDL', 'CTL', 'ARM', 'ABY', 'ARB', 'CRD', 'PRS', 'NUN',
      'MSL', 'CPN', 'SGT', 'GRD', 'ARC', 'KNT', 'RAM', 'MAC', 'LNC', 'HCT', 'SWD', 'SNT', 'WZD'];
    // Combined I/H pieces (includes EPS)
    const INTERMEDIATE_AND_HIGH = [...INTERMEDIATE_PIECES, 'EPS', ...HIGH_PIECES];
    // Low level pieces for 9x9
    const LOW_PIECES = ['PWN', 'KNV'];

    // White pieces: back rank row 8, pawn row 7
    // Black pieces: back rank row 0, pawn row 1
    const backRank = color === 'white' ? 8 : 0;
    const pawnRank = color === 'white' ? 7 : 1;

    if (row === backRank) {
      // Back rank positions (9 columns: 0-8 = a-i)
      if (col === 0 || col === 8) return HIGH_PIECES; // a1/i1 or a9/i9 - corners (H)
      if (col === 1 || col === 7) return INTERMEDIATE_PIECES; // b1/h1 or b9/h9 - (I)
      if (col === 2 || col === 6) return INTERMEDIATE_AND_HIGH; // c1/g1 or c9/g9 - (I/H)
      if (col === 3 || col === 5) return HIGH_PIECES; // d1/f1 or d9/f9 - (H)
      if (col === 4) return ['KNG', 'EPR']; // e1 or e9 - king square (C)
    }
    if (row === pawnRank) {
      return LOW_PIECES; // Pawn row - PWN and KNV allowed
    }
    return []; // Other rows - return empty (will be treated as "any piece allowed")
  };

  // Validate piece placement for 9x9 board
  const validatePiecePlacement9x9 = (): ValidationIssue[] => {
    const placementIssues: ValidationIssue[] = [];

    pieces.forEach((piece) => {
      const { row, col } = piece.position;
      const allowedPieces = getAllowedPieces9x9(row, col, piece.color);

      // If allowedPieces is empty, any piece is allowed (middle rows)
      if (allowedPieces.length > 0 && !allowedPieces.includes(piece.abbreviation)) {
        const colLetter = String.fromCharCode(97 + col); // a-i
        const rowNumber = 9 - row; // Convert to chess notation
        const pieceLevel = getPieceLevel(piece.abbreviation);
        const levelNames: Record<PieceLevel, string> = {
          'L': 'Low', 'I': 'Intermediate', 'H': 'High', 'C': 'Checkmate'
        };
        placementIssues.push({
          type: 'error',
          message: `${piece.abbreviation} (${levelNames[pieceLevel]}) not allowed at ${colLetter}${rowNumber}`
        });
      }
    });

    return placementIssues;
  };

  // Get allowed pieces for a position on 10x10 board based on exact table rules
  const getAllowedPieces10x10 = (row: number, col: number, color: 'white' | 'black'): string[] => {
    // High level pieces (includes EPS per 10x10 table)
    const HIGH_PIECES = ['EPS', 'QUN', 'FTS', 'CSL', 'POP', 'BSP', 'CTP', 'AGL'];
    // Intermediate level pieces
    const INTERMEDIATE_PIECES = ['DUK', 'CHL', 'CDL', 'CTL', 'ARM', 'ABY', 'ARB', 'CRD', 'PRS', 'NUN',
      'MSL', 'CPN', 'SGT', 'GRD', 'ARC', 'KNT', 'RAM', 'MAC', 'LNC', 'HCT', 'SWD', 'SNT', 'WZD'];
    // Combined I/H pieces
    const INTERMEDIATE_AND_HIGH = [...INTERMEDIATE_PIECES, ...HIGH_PIECES];
    // Low level pieces for 10x10 (only KNV and SQR per table - no PWN)
    const LOW_PIECES = ['KNV', 'SQR'];
    // Checkmate pieces
    const CHECKMATE_PIECES = ['KNG', 'EPR'];

    // White pieces: back rank row 9, pawn row 8
    // Black pieces: back rank row 0, pawn row 1
    const backRank = color === 'white' ? 9 : 0;
    const pawnRank = color === 'white' ? 8 : 1;

    if (row === backRank) {
      // Back rank positions (10 columns: 0-9 = a-j)
      if (col === 0 || col === 9) return HIGH_PIECES; // a1/j1 or a10/j10 - corners (H)
      if (col === 1 || col === 8) return INTERMEDIATE_PIECES; // b1/i1 or b10/i10 - (I)
      if (col === 2 || col === 7) return INTERMEDIATE_AND_HIGH; // c1/h1 or c10/h10 - (I/H)
      if (col === 3 || col === 6) return HIGH_PIECES; // d1/g1 or d10/g10 - (H)
      if (col === 4) return CHECKMATE_PIECES; // e1 or e10 - king square (C)
      if (col === 5) return HIGH_PIECES; // f1 or f10 - (H)
    }
    if (row === pawnRank) {
      return LOW_PIECES; // Pawn row - only KNV and SQR allowed
    }
    return []; // Other rows - return empty (will be treated as "any piece allowed")
  };

  // Validate piece placement for 10x10 board
  const validatePiecePlacement10x10 = (): ValidationIssue[] => {
    const placementIssues: ValidationIssue[] = [];

    pieces.forEach((piece) => {
      const { row, col } = piece.position;
      const allowedPieces = getAllowedPieces10x10(row, col, piece.color);

      // If allowedPieces is empty, any piece is allowed (middle rows)
      if (allowedPieces.length > 0 && !allowedPieces.includes(piece.abbreviation)) {
        const colLetter = String.fromCharCode(97 + col); // a-j
        const rowNumber = 10 - row; // Convert to chess notation
        const pieceLevel = getPieceLevel(piece.abbreviation);
        const levelNames: Record<PieceLevel, string> = {
          'L': 'Low', 'I': 'Intermediate', 'H': 'High', 'C': 'Checkmate'
        };
        placementIssues.push({
          type: 'error',
          message: `${piece.abbreviation} (${levelNames[pieceLevel]}) not allowed at ${colLetter}${rowNumber}`
        });
      }
    });

    return placementIssues;
  };

  // Get allowed pieces for a position on 11x11 board based on exact table rules
  const getAllowedPieces11x11 = (row: number, col: number, color: 'white' | 'black'): string[] => {
    // High level pieces (includes EPS per 11x11 table)
    const HIGH_PIECES = ['EPS', 'QUN', 'FTS', 'CSL', 'POP', 'BSP', 'CTP', 'AGL'];
    // Intermediate level pieces
    const INTERMEDIATE_PIECES = ['DUK', 'CHL', 'CDL', 'CTL', 'ARM', 'ABY', 'ARB', 'CRD', 'PRS', 'NUN',
      'MSL', 'CPN', 'SGT', 'GRD', 'ARC', 'KNT', 'RAM', 'MAC', 'LNC', 'HCT', 'SWD', 'SNT', 'WZD'];
    // Combined I/H pieces
    const INTERMEDIATE_AND_HIGH = [...INTERMEDIATE_PIECES, ...HIGH_PIECES];
    // Low level pieces for 11x11 (only KNV and SQR per table)
    const LOW_PIECES = ['KNV', 'SQR'];
    // Checkmate pieces
    const CHECKMATE_PIECES = ['KNG', 'EPR'];

    // White pieces: back rank row 10, row 2 is row 9, pawn row 8
    // Black pieces: back rank row 0, row 2 is row 1, pawn row 2
    const backRank = color === 'white' ? 10 : 0;
    const row2 = color === 'white' ? 9 : 1;
    const pawnRank = color === 'white' ? 8 : 2;

    if (row === backRank) {
      // Back rank positions (11 columns: 0-10 = a-k)
      if (col === 0 || col === 10) return HIGH_PIECES; // a1/k1 or a11/k11 - corners (H)
      if (col === 1 || col === 9) return INTERMEDIATE_AND_HIGH; // b1/j1 or b11/j11 - (I/H)
      if (col === 2 || col === 8) return INTERMEDIATE_AND_HIGH; // c1/i1 or c11/i11 - (I/H)
      if (col === 3 || col === 7) return HIGH_PIECES; // d1/h1 or d11/h11 - (H)
      if (col === 4 || col === 6) return HIGH_PIECES; // e1/g1 or e11/g11 - (H)
      if (col === 5) return CHECKMATE_PIECES; // f1 or f11 - king square (C)
    }
    if (row === row2) {
      // Row 2 positions
      if (col === 0 || col === 1 || col === 2) return INTERMEDIATE_PIECES; // a2/b2/c2 or a10/b10/c10 - (I)
      if (col === 3 || col === 7) return INTERMEDIATE_AND_HIGH; // d2/h2 or d10/h10 - (I/H)
      if (col === 4 || col === 6) return INTERMEDIATE_AND_HIGH; // e2/g2 or e10/g10 - (I/H)
      if (col === 5) return HIGH_PIECES; // f2 or f10 - (H)
      if (col === 8 || col === 9 || col === 10) return INTERMEDIATE_PIECES; // i2/j2/k2 or i10/j10/k10 - (I)
    }
    if (row === pawnRank) {
      return LOW_PIECES; // Pawn row - only KNV and SQR allowed
    }
    return []; // Other rows - return empty (will be treated as "any piece allowed")
  };

  // Validate piece placement for 11x11 board
  const validatePiecePlacement11x11 = (): ValidationIssue[] => {
    const placementIssues: ValidationIssue[] = [];

    pieces.forEach((piece) => {
      const { row, col } = piece.position;
      const allowedPieces = getAllowedPieces11x11(row, col, piece.color);

      // If allowedPieces is empty, any piece is allowed (middle rows)
      if (allowedPieces.length > 0 && !allowedPieces.includes(piece.abbreviation)) {
        const colLetter = String.fromCharCode(97 + col); // a-k
        const rowNumber = 11 - row; // Convert to chess notation
        const pieceLevel = getPieceLevel(piece.abbreviation);
        const levelNames: Record<PieceLevel, string> = {
          'L': 'Low', 'I': 'Intermediate', 'H': 'High', 'C': 'Checkmate'
        };
        placementIssues.push({
          type: 'error',
          message: `${piece.abbreviation} (${levelNames[pieceLevel]}) not allowed at ${colLetter}${rowNumber}`
        });
      }
    });

    return placementIssues;
  };

  // Get allowed pieces for a position on 12x12 board based on exact table rules
  const getAllowedPieces12x12 = (row: number, col: number, color: 'white' | 'black'): string[] => {
    // High level pieces (includes EPS per 12x12 table)
    const HIGH_PIECES = ['EPS', 'QUN', 'FTS', 'CSL', 'POP', 'BSP', 'CTP', 'AGL'];
    // Intermediate level pieces
    const INTERMEDIATE_PIECES = ['DUK', 'CHL', 'CDL', 'CTL', 'ARM', 'ABY', 'ARB', 'CRD', 'PRS', 'NUN',
      'MSL', 'CPN', 'SGT', 'GRD', 'ARC', 'KNT', 'RAM', 'MAC', 'LNC', 'HCT', 'SWD', 'SNT', 'WZD'];
    // Combined I/H pieces
    const INTERMEDIATE_AND_HIGH = [...INTERMEDIATE_PIECES, ...HIGH_PIECES];
    // Low level pieces for 12x12 (only KNV and SQR per table)
    const LOW_PIECES = ['KNV', 'SQR'];
    // Checkmate pieces
    const CHECKMATE_PIECES = ['KNG', 'EPR'];

    // White pieces: back rank row 11, row 2 is row 10, pawn row 9
    // Black pieces: back rank row 0, row 2 is row 1, pawn row 2
    const backRank = color === 'white' ? 11 : 0;
    const row2 = color === 'white' ? 10 : 1;
    const pawnRank = color === 'white' ? 9 : 2;

    if (row === backRank) {
      // Back rank positions (12 columns: 0-11 = a-l)
      if (col === 0 || col === 11) return HIGH_PIECES; // a1/l1 or a12/l12 - corners (H)
      if (col === 1 || col === 10) return INTERMEDIATE_AND_HIGH; // b1/k1 or b12/k12 - (I/H)
      if (col === 2 || col === 9) return INTERMEDIATE_AND_HIGH; // c1/j1 or c12/j12 - (I/H)
      if (col === 3 || col === 8) return HIGH_PIECES; // d1/i1 or d12/i12 - (H)
      if (col === 4 || col === 7) return HIGH_PIECES; // e1/h1 or e12/h12 - (H)
      if (col === 5) return CHECKMATE_PIECES; // f1 or f12 - king square (C)
      if (col === 6) return HIGH_PIECES; // g1 or g12 - (H)
    }
    if (row === row2) {
      // Row 2 positions
      if (col === 0 || col === 1 || col === 2) return INTERMEDIATE_PIECES; // a2/b2/c2 or a11/b11/c11 - (I)
      if (col === 3 || col === 8) return INTERMEDIATE_AND_HIGH; // d2/i2 or d11/i11 - (I/H)
      if (col === 4 || col === 5 || col === 6 || col === 7) return HIGH_PIECES; // e2/f2/g2/h2 or e11/f11/g11/h11 - (H)
      if (col === 9 || col === 10 || col === 11) return INTERMEDIATE_PIECES; // j2/k2/l2 or j11/k11/l11 - (I)
    }
    if (row === pawnRank) {
      return LOW_PIECES; // Pawn row - only KNV and SQR allowed
    }
    return []; // Other rows - return empty (will be treated as "any piece allowed")
  };

  // Validate piece placement for 12x12 board
  const validatePiecePlacement12x12 = (): ValidationIssue[] => {
    const placementIssues: ValidationIssue[] = [];

    pieces.forEach((piece) => {
      const { row, col } = piece.position;
      const allowedPieces = getAllowedPieces12x12(row, col, piece.color);

      // If allowedPieces is empty, any piece is allowed (middle rows)
      if (allowedPieces.length > 0 && !allowedPieces.includes(piece.abbreviation)) {
        const colLetter = String.fromCharCode(97 + col); // a-l
        const rowNumber = 12 - row; // Convert to chess notation
        const pieceLevel = getPieceLevel(piece.abbreviation);
        const levelNames: Record<PieceLevel, string> = {
          'L': 'Low', 'I': 'Intermediate', 'H': 'High', 'C': 'Checkmate'
        };
        placementIssues.push({
          type: 'error',
          message: `${piece.abbreviation} (${levelNames[pieceLevel]}) not allowed at ${colLetter}${rowNumber}`
        });
      }
    });

    return placementIssues;
  };

  const validateConfiguration = (): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];
    const whitePieces: PlacedPiece[] = [];
    const blackPieces: PlacedPiece[] = [];

    const midPoint = Math.floor(boardSize / 2);
    pieces.forEach((piece) => {
      if (piece.position.row < midPoint) {
        blackPieces.push(piece);
      } else {
        whitePieces.push(piece);
      }
    });

    const whiteCheckmatePieces = whitePieces.filter(
      (p) => p.type === "king" || p.type === "emperor"
    );
    const blackCheckmatePieces = blackPieces.filter(
      (p) => p.type === "king" || p.type === "emperor"
    );

    if (whiteCheckmatePieces.length === 0) {
      issues.push({
        type: "error",
        message: "White needs a checkmate piece (King or Emperor)",
      });
    }
    if (blackCheckmatePieces.length === 0) {
      issues.push({
        type: "error",
        message: "Black needs a checkmate piece (King or Emperor)",
      });
    }

    if (whiteCheckmatePieces.length > 1) {
      issues.push({
        type: "error",
        message: "White can only have one checkmate piece",
      });
    }
    if (blackCheckmatePieces.length > 1) {
      issues.push({
        type: "error",
        message: "Black can only have one checkmate piece",
      });
    }

    const pieceCountDiff = Math.abs(whitePieces.length - blackPieces.length);
    if (pieceCountDiff > 2) {
      issues.push({
        type: "warning",
        message: `Piece count imbalance: ${pieceCountDiff} difference`,
      });
    }

    // Row restriction validation for 8x8, 9x9, 10x10, 11x11, and 12x12 boards
    if (boardSize === 8 || boardSize === 9 || boardSize === 10 || boardSize === 11 || boardSize === 12) {
      // 2 rows for 8x8, 9x9, 10x10; 3 rows for 11x11, 12x12
      const allowedRows = (boardSize === 11 || boardSize === 12) ? 3 : (boardSize === 10 ? 2 : guidelines.rows);

      // Check white pieces are only in allowed rows (bottom rows)
      const whiteInvalidRows = whitePieces.filter(p => {
        const rowFromBottom = boardSize - 1 - p.position.row;
        return rowFromBottom >= allowedRows;
      });

      // Check black pieces are only in allowed rows (top rows)
      const blackInvalidRows = blackPieces.filter(p => {
        return p.position.row >= allowedRows;
      });

      if (whiteInvalidRows.length > 0) {
        issues.push({
          type: "error",
          message: `White has ${whiteInvalidRows.length} piece(s) beyond allowed rows`,
        });
      }

      if (blackInvalidRows.length > 0) {
        issues.push({
          type: "error",
          message: `Black has ${blackInvalidRows.length} piece(s) beyond allowed rows`,
        });
      }
    }

    // Add 8x8 board piece placement validation
    if (boardSize === 8) {
      const placementIssues = validatePiecePlacement8x8();
      issues.push(...placementIssues);
    }

    // Add 9x9 board piece placement validation
    if (boardSize === 9) {
      const placementIssues = validatePiecePlacement9x9();
      issues.push(...placementIssues);
    }

    // Add 10x10 board piece placement validation
    if (boardSize === 10) {
      const placementIssues = validatePiecePlacement10x10();
      issues.push(...placementIssues);
    }

    // Add 11x11 board piece placement validation
    if (boardSize === 11) {
      const placementIssues = validatePiecePlacement11x11();
      issues.push(...placementIssues);
    }

    // Add 12x12 board piece placement validation
    if (boardSize === 12) {
      const placementIssues = validatePiecePlacement12x12();
      issues.push(...placementIssues);
    }

    return issues;
  };

  useEffect(() => {
    setValidationIssues(validateConfiguration());
  }, [pieces, boardSize]);

  const getClassColor = (pieceClass: PieceClass): string => {
    const colors: Record<PieceClass, string> = {
      ruling: "#4169E1",
      architectural: "#a38b5eff",
      religious: "#9370DB",
      military: "#DC143C",
      weaponry: "#bd96b7ff",
      special: "#FF8C00",
    };
    return colors[pieceClass];
  };

  const getPieceIcon = (
    abbreviation: string,
    color: "white" | "black"
  ): string => {
    return `/pieces/${abbreviation.toLowerCase()}.png`;
  };

  // Level filter options for Piece Catalog
  const levelOptions: Array<{ key: 'L' | 'I' | 'H' | 'C'; label: string; color: string }> = [
    { key: 'L', label: 'Low', color: '#607D8B' },
    { key: 'I', label: 'Intermediate', color: '#2196F3' },
    { key: 'H', label: 'High', color: '#9C27B0' },
    { key: 'C', label: 'Checkmate', color: '#F44336' },
  ];

  const toggleLevelFilter = (level: 'L' | 'I' | 'H' | 'C') => {
    // Exclusive selection - deselect all other levels when one is selected
    setLevelFilters({
      L: level === 'L',
      I: level === 'I',
      H: level === 'H',
      C: level === 'C',
    });
    // Clear selected piece when switching levels
    setSelectedPieceType(null);
  };

  // Check if a specific level's squares are all filled
  const isLevelFilled = (level: string): boolean => {
    let availableSquares = 0;
    let filledSquares = 0;

    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        const badge = getSquareLevelBadge(row, col, boardSize);
        if (badge) {
          const allowedLevels = badge.level.split('/');
          if (allowedLevels.includes(level)) {
            availableSquares++;
            const piece = pieces.get(`${row}-${col}`);
            if (piece) {
              filledSquares++;
            }
          }
        }
      }
    }

    return availableSquares > 0 && filledSquares >= availableSquares;
  };

  // Get currently active level filters
  const getActiveLevels = (): string[] => {
    return Object.entries(levelFilters)
      .filter(([_, active]) => active)
      .map(([level]) => level);
  };

  // Get current step instruction based on user's progress
  const getCurrentStepInstruction = (): { step: number; message: React.ReactNode; isMultiLine?: boolean } | null => {
    // Only show instructions when not in loaded variant mode, or when editing is enabled
    if (loadedVariantId && !isEditMode) {
      return null;
    }

    // Check if all active level filters are completely filled
    const activeLevels = getActiveLevels();
    const allActivesFilled = activeLevels.length > 0 && activeLevels.every(level => isLevelFilled(level));

    if (allActivesFilled) {
      return {
        step: 0,
        message: <>Select new level (<span style={{ backgroundColor: '#607D8B', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', marginRight: '2px' }}>L</span><span style={{ backgroundColor: '#2196F3', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', marginRight: '2px' }}>I</span><span style={{ backgroundColor: '#9C27B0', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', marginRight: '2px' }}>H</span><span style={{ backgroundColor: '#F44336', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px' }}>C</span>)</>
      };
    }

    if (!selectedPieceType) {
      return {
        step: 1,
        message: "Scroll down in the Piece Catalog to find and select a piece (it will highlight)"
      };
    }

    if (pieces.size === 0) {
      return {
        step: 2,
        message: "Move cursor over desired board square and click to place the piece"
      };
    }

    return {
      step: 3,
      message: "Click to place another piece on an open square with correct level:",
      isMultiLine: true
    };
  };

  const currentStep = getCurrentStepInstruction();

  return (
    <div className="variant-creator-container">
      <PageHeader
        icon={TAB_DESCRIPTIONS.variants.icon}
        title={TAB_DESCRIPTIONS.variants.label}
        description={TAB_DESCRIPTIONS.variants.description}
      />

      {/* Variant Profiles Panel */}
      <VariantProfiles
        variants={customVariants}
        onRefresh={refreshVariants}
        onLoadVariant={loadVariantIntoEditor}
        currentVariantData={getCurrentVariantData()}
        hasValidConfiguration={hasValidConfiguration()}
        loadedVariantId={loadedVariantId}
        isEditMode={isEditMode}
        onSaveComplete={() => setSelectedPieceType(null)}
      />

      {/* Edit Mode Panel - Shows when a variant is loaded */}
      {loadedVariantId && (
        <div className="edit-mode-panel">
          <label className="edit-mode-checkbox">
            <input
              type="checkbox"
              checked={isEditMode}
              onChange={(e) => setIsEditMode(e.target.checked)}
            />
            <span className="edit-mode-label">
              Enable editing - allows you to modify and re-save the loaded variant
            </span>
          </label>
          {isEditMode && (
            <p className="edit-mode-hint">
              Edit mode enabled. Select a piece from Piece Catalog to begin editing. Make changes and use "Save Current" to update or create a new variant.
            </p>
          )}
        </div>
      )}

      <div className="creator-layout">
        {/* Left Panel - Guidelines */}
        <div className="guidelines-panel">
          <h3>📋 Guidelines</h3>

          <div className="guideline-section">
            <ul className="recommendations-list">
              {guidelines.recommendations.map((rec: string, idx: number) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>

          <div className="guideline-section placement-rules-section">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={showPlacementRulesModal}
                onChange={(e) => setShowPlacementRulesModal(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show {boardSize}x{boardSize} Placement Rules
            </label>
          </div>

          <div className="guideline-section validation-section">
            <h4>Validation</h4>
            <div className="validation-list">
              {/* Total piece count */}
              {(() => {
                const whitePieceCount = Array.from(pieces.values()).filter(p => p.color === 'white').length;
                const blackPieceCount = Array.from(pieces.values()).filter(p => p.color === 'black').length;
                const totalRequired = guidelines.pieces * 2; // Total for both sides
                const perSideRequired = guidelines.pieces;
                const totalPlaced = pieces.size;
                const meetsTotal = totalPlaced === totalRequired;
                const meetsWhite = whitePieceCount === perSideRequired;
                const meetsBlack = blackPieceCount === perSideRequired;

                return (
                  <>
                    <div className={`validation-item ${meetsTotal ? 'valid' : 'error'}`}>
                      {meetsTotal ? '✅' : '❌'} Total pieces: {totalPlaced}/{totalRequired}
                    </div>
                    <div className={`validation-item ${meetsWhite ? 'valid' : 'error'}`}>
                      {meetsWhite ? '✅' : '❌'} White pieces: {whitePieceCount}/{perSideRequired}
                    </div>
                    <div className={`validation-item ${meetsBlack ? 'valid' : 'error'}`}>
                      {meetsBlack ? '✅' : '❌'} Black pieces: {blackPieceCount}/{perSideRequired}
                    </div>
                  </>
                );
              })()}
              {/* Both rows filled validation for 8x8 and 9x9 */}
              {(boardSize === 8 || boardSize === 9) && (() => {
                const whiteBackRank = boardSize - 1;
                const whitePawnRank = boardSize - 2;
                const blackBackRank = 0;
                const blackPawnRank = 1;

                const whiteBackCount = Array.from(pieces.values()).filter(p => p.position.row === whiteBackRank).length;
                const whitePawnCount = Array.from(pieces.values()).filter(p => p.position.row === whitePawnRank).length;
                const blackBackCount = Array.from(pieces.values()).filter(p => p.position.row === blackBackRank).length;
                const blackPawnCount = Array.from(pieces.values()).filter(p => p.position.row === blackPawnRank).length;

                const whiteBackFilled = whiteBackCount === boardSize;
                const whitePawnFilled = whitePawnCount === boardSize;
                const blackBackFilled = blackBackCount === boardSize;
                const blackPawnFilled = blackPawnCount === boardSize;
                const allRowsFilled = whiteBackFilled && whitePawnFilled && blackBackFilled && blackPawnFilled;

                return (
                  <>
                    <div className={`validation-item ${allRowsFilled ? 'valid' : 'error'}`}>
                      {allRowsFilled ? '✅' : '❌'} All rows filled
                    </div>
                    {!allRowsFilled && (
                      <div style={{ fontSize: '11px', marginLeft: '20px', color: '#666' }}>
                        {!whiteBackFilled && <div>White back rank: {whiteBackCount}/{boardSize}</div>}
                        {!whitePawnFilled && <div>White pawn row: {whitePawnCount}/{boardSize}</div>}
                        {!blackBackFilled && <div>Black back rank: {blackBackCount}/{boardSize}</div>}
                        {!blackPawnFilled && <div>Black pawn row: {blackPawnCount}/{boardSize}</div>}
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Both rows filled validation for 10x10 */}
              {boardSize === 10 && (() => {
                const whiteBackRank = 9;
                const whitePawnRank = 8;
                const blackBackRank = 0;
                const blackPawnRank = 1;

                const whiteBackCount = Array.from(pieces.values()).filter(p => p.position.row === whiteBackRank).length;
                const whitePawnCount = Array.from(pieces.values()).filter(p => p.position.row === whitePawnRank).length;
                const blackBackCount = Array.from(pieces.values()).filter(p => p.position.row === blackBackRank).length;
                const blackPawnCount = Array.from(pieces.values()).filter(p => p.position.row === blackPawnRank).length;

                const whiteBackFilled = whiteBackCount === boardSize;
                const whitePawnFilled = whitePawnCount === boardSize;
                const blackBackFilled = blackBackCount === boardSize;
                const blackPawnFilled = blackPawnCount === boardSize;
                const allRowsFilled = whiteBackFilled && whitePawnFilled && blackBackFilled && blackPawnFilled;

                return (
                  <>
                    <div className={`validation-item ${allRowsFilled ? 'valid' : 'error'}`}>
                      {allRowsFilled ? '✅' : '❌'} All rows filled
                    </div>
                    {!allRowsFilled && (
                      <div style={{ fontSize: '11px', marginLeft: '20px', color: '#666' }}>
                        {!whiteBackFilled && <div>White back rank: {whiteBackCount}/{boardSize}</div>}
                        {!whitePawnFilled && <div>White pawn row: {whitePawnCount}/{boardSize}</div>}
                        {!blackBackFilled && <div>Black back rank: {blackBackCount}/{boardSize}</div>}
                        {!blackPawnFilled && <div>Black pawn row: {blackPawnCount}/{boardSize}</div>}
                      </div>
                    )}
                  </>
                );
              })()}
              {/* All rows filled validation for 11x11 (3 rows per side) */}
              {boardSize === 11 && (() => {
                const whiteBackRank = 10;
                const whiteRow2 = 9;
                const whitePawnRank = 8;
                const blackBackRank = 0;
                const blackRow2 = 1;
                const blackPawnRank = 2;

                const whiteBackCount = Array.from(pieces.values()).filter(p => p.position.row === whiteBackRank).length;
                const whiteRow2Count = Array.from(pieces.values()).filter(p => p.position.row === whiteRow2).length;
                const whitePawnCount = Array.from(pieces.values()).filter(p => p.position.row === whitePawnRank).length;
                const blackBackCount = Array.from(pieces.values()).filter(p => p.position.row === blackBackRank).length;
                const blackRow2Count = Array.from(pieces.values()).filter(p => p.position.row === blackRow2).length;
                const blackPawnCount = Array.from(pieces.values()).filter(p => p.position.row === blackPawnRank).length;

                const whiteBackFilled = whiteBackCount === boardSize;
                const whiteRow2Filled = whiteRow2Count === boardSize;
                const whitePawnFilled = whitePawnCount === boardSize;
                const blackBackFilled = blackBackCount === boardSize;
                const blackRow2Filled = blackRow2Count === boardSize;
                const blackPawnFilled = blackPawnCount === boardSize;
                const allRowsFilled = whiteBackFilled && whiteRow2Filled && whitePawnFilled && blackBackFilled && blackRow2Filled && blackPawnFilled;

                return (
                  <>
                    <div className={`validation-item ${allRowsFilled ? 'valid' : 'error'}`}>
                      {allRowsFilled ? '✅' : '❌'} All rows filled
                    </div>
                    {!allRowsFilled && (
                      <div style={{ fontSize: '11px', marginLeft: '20px', color: '#666' }}>
                        {!whiteBackFilled && <div>White back rank: {whiteBackCount}/{boardSize}</div>}
                        {!whiteRow2Filled && <div>White row 2: {whiteRow2Count}/{boardSize}</div>}
                        {!whitePawnFilled && <div>White pawn row: {whitePawnCount}/{boardSize}</div>}
                        {!blackBackFilled && <div>Black back rank: {blackBackCount}/{boardSize}</div>}
                        {!blackRow2Filled && <div>Black row 2: {blackRow2Count}/{boardSize}</div>}
                        {!blackPawnFilled && <div>Black pawn row: {blackPawnCount}/{boardSize}</div>}
                      </div>
                    )}
                  </>
                );
              })()}
              {/* All rows filled validation for 12x12 (3 rows per side) */}
              {boardSize === 12 && (() => {
                const whiteBackRank = 11;
                const whiteRow2 = 10;
                const whitePawnRank = 9;
                const blackBackRank = 0;
                const blackRow2 = 1;
                const blackPawnRank = 2;

                const whiteBackCount = Array.from(pieces.values()).filter(p => p.position.row === whiteBackRank).length;
                const whiteRow2Count = Array.from(pieces.values()).filter(p => p.position.row === whiteRow2).length;
                const whitePawnCount = Array.from(pieces.values()).filter(p => p.position.row === whitePawnRank).length;
                const blackBackCount = Array.from(pieces.values()).filter(p => p.position.row === blackBackRank).length;
                const blackRow2Count = Array.from(pieces.values()).filter(p => p.position.row === blackRow2).length;
                const blackPawnCount = Array.from(pieces.values()).filter(p => p.position.row === blackPawnRank).length;

                const whiteBackFilled = whiteBackCount === boardSize;
                const whiteRow2Filled = whiteRow2Count === boardSize;
                const whitePawnFilled = whitePawnCount === boardSize;
                const blackBackFilled = blackBackCount === boardSize;
                const blackRow2Filled = blackRow2Count === boardSize;
                const blackPawnFilled = blackPawnCount === boardSize;
                const allRowsFilled = whiteBackFilled && whiteRow2Filled && whitePawnFilled && blackBackFilled && blackRow2Filled && blackPawnFilled;

                return (
                  <>
                    <div className={`validation-item ${allRowsFilled ? 'valid' : 'error'}`}>
                      {allRowsFilled ? '✅' : '❌'} All rows filled
                    </div>
                    {!allRowsFilled && (
                      <div style={{ fontSize: '11px', marginLeft: '20px', color: '#666' }}>
                        {!whiteBackFilled && <div>White back rank: {whiteBackCount}/{boardSize}</div>}
                        {!whiteRow2Filled && <div>White row 2: {whiteRow2Count}/{boardSize}</div>}
                        {!whitePawnFilled && <div>White pawn row: {whitePawnCount}/{boardSize}</div>}
                        {!blackBackFilled && <div>Black back rank: {blackBackCount}/{boardSize}</div>}
                        {!blackRow2Filled && <div>Black row 2: {blackRow2Count}/{boardSize}</div>}
                        {!blackPawnFilled && <div>Black pawn row: {blackPawnCount}/{boardSize}</div>}
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Balanced sides */}
              <div className={`validation-item ${Math.abs(Array.from(pieces.values()).filter(p => p.color === 'white').length - Array.from(pieces.values()).filter(p => p.color === 'black').length) <= 2 ? 'valid' : 'warning'}`}>
                {Math.abs(Array.from(pieces.values()).filter(p => p.color === 'white').length - Array.from(pieces.values()).filter(p => p.color === 'black').length) <= 2 ? '✅' : '⚠️'} Balanced piece count
              </div>
            </div>
            {validationIssues.length > 0 && (
              <div className="validation-issues" style={{ marginTop: '10px' }}>
                {validationIssues.map((issue, idx) => (
                  <div key={idx} className={`validation-item ${issue.type}`}>
                    {issue.type === "error" ? "❌" : "⚠️"} {issue.message}
                  </div>
                ))}
              </div>
            )}
            {hasValidConfiguration() && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: '#e8f5e9',
                borderRadius: '6px',
                border: '1px solid #4CAF50'
              }}>
                <span style={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '13px' }}>
                  ✅ You can save current Variant Profile now
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Middle Panel - Chess Board */}
        <div className="board-panel">
          <div className="board-controls">
            <div className="control-group">
              <label>Board Size:</label>
              <select
                value={boardSize}
                onChange={(e) => setBoardSize(Number(e.target.value))}
                className="size-selector"
              >
                {[8, 9, 10, 11, 12].map((size) => (
                  <option key={size} value={size}>
                    {size}×{size}
                  </option>
                ))}
              </select>
            </div>
            <button onClick={clearBoard} className="clear-btn">
              🗑️ Clear Board
            </button>
            <div className="control-group">
              <label>
                <input
                  type="checkbox"
                  checked={symmetryMode}
                  onChange={(e) => setSymmetryMode(e.target.checked)}
                />
                Symmetry Mode
              </label>
            </div>
            {currentStep && !levelMismatchError && (
              <div className="step-hint-container board-step-hint">
                <span className="step-hint-icon">♟</span>
                <div className="step-hint-content">
                  <div className="step-hint-title">How to Place Pieces on Chess Board</div>
                  <div className="step-hint-message">
                    {currentStep.message}
                    {currentStep.isMultiLine && (
                      <ul className="step-hint-bullets">
                        <li>or click an occupied square to remove an already placed piece</li>
                        <li>or select another piece to place on chess board</li>
                        <li>or select new level (<span style={{ backgroundColor: '#607D8B', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', marginRight: '2px' }}>L</span><span style={{ backgroundColor: '#2196F3', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', marginRight: '2px' }}>I</span><span style={{ backgroundColor: '#9C27B0', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', marginRight: '2px' }}>H</span><span style={{ backgroundColor: '#F44336', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px' }}>C</span>)</li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            {levelMismatchError && (
              <div className="level-mismatch-error board-step-hint">
                {levelMismatchError}
              </div>
            )}
          </div>

          <div className="board-wrapper">
            <div className="rank-labels">
              {Array.from({ length: boardSize }, (_, i) => (
                <div key={i} className="rank-label">
                  {boardSize - i}
                </div>
              ))}
            </div>

            <div
              className="chess-board"
              style={{
                gridTemplateColumns: `repeat(${boardSize}, 60px)`,
                gridTemplateRows: `repeat(${boardSize}, 60px)`,
              }}
            >
              {Array.from({ length: boardSize }, (_, row) =>
                Array.from({ length: boardSize }, (_, col) => {
                  const piece = getPieceAt(row, col);
                  const levelBadge = getSquareLevelBadge(row, col, boardSize);
                  return (
                    <div
                      key={`${row}-${col}`}
                      className={`square ${
                        (row + col) % 2 === 0 ? "light" : "dark"
                      }`}
                      style={{
                        backgroundColor:
                          (row + col) % 2 === 0
                            ? lightSquareColor
                            : darkSquareColor,
                        position: 'relative',
                      }}
                      onClick={() => handleSquareClick(row, col)}
                    >
                      {levelBadge && (
                        <div
                          className="level-badge"
                          style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            backgroundColor: levelBadge.color,
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            padding: '2px 4px',
                            borderRadius: '3px',
                            zIndex: 1,
                            lineHeight: '1',
                            opacity: 0.9,
                          }}
                        >
                          {levelBadge.level}
                        </div>
                      )}
                      {piece && (
                        <div className="piece">
                          <img
                            src={getPieceIcon(
                              piece.abbreviation,
                              row < boardSize / 2 ? "black" : "white"
                            )}
                            alt={piece.type}
                            className={`piece-image ${
                              row < boardSize / 2
                                ? "black-piece"
                                : "white-piece"
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="file-labels-bottom">
            {Array.from({ length: boardSize }, (_, i) => (
              <div key={i} className="file-label-bottom">
                {String.fromCharCode(97 + i)}
              </div>
            ))}
          </div>

          <div className="board-stats">
            <span>
              ⚪ White Pieces:{" "}
              {
                Array.from(pieces.values()).filter(
                  (p) => p.position.row >= boardSize / 2
                ).length
              }
            </span>
            <span>⚪ White Value: {whiteValue.toFixed(2)}</span>
            <span>
              ⚫ Black Pieces:{" "}
              {
                Array.from(pieces.values()).filter(
                  (p) => p.position.row < boardSize / 2
                ).length
              }
            </span>
            <span>⚫ Black Value: {blackValue.toFixed(2)}</span>
            <span>Total: {pieces.size}</span>
          </div>
        </div>

        {/* Right Panel - Piece Catalog */}
        <div className="piece-library-panel">
          <h3>🎭 Piece Catalog</h3>

          <div className="usage-instructions">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={showInstructionsModal}
                onChange={(e) => setShowInstructionsModal(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Level Guide
            </label>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '10px 0' }} />

          <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
            Use "Symmetry Mode" to automatically place matching black pieces
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '10px 0' }} />

          <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
            Scroll down to find and select a piece (it will highlight).
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '10px 0' }} />

          <div className="level-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
            {levelOptions.map((option) => (
              <label
                key={option.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                <input
                  type="checkbox"
                  checked={levelFilters[option.key]}
                  onChange={() => toggleLevelFilter(option.key)}
                  style={{ display: 'none' }}
                />
                <span
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '3px',
                    backgroundColor: levelFilters[option.key] ? '#4CAF50' : '#fff',
                    border: levelFilters[option.key] ? '1px solid #4CAF50' : '1px solid #999',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  {levelFilters[option.key] ? '✓' : ''}
                </span>
                <span
                  style={{
                    backgroundColor: option.color,
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    minWidth: '14px',
                    textAlign: 'center',
                  }}
                >
                  {option.key}
                </span>
                <span style={{ fontSize: '12px' }}>{option.label}</span>
              </label>
            ))}
            <span style={{ fontSize: '11px', color: '#666', alignSelf: 'center', marginLeft: '5px' }}>
              ({piecesToDisplay.length} pieces)
            </span>
          </div>

          <div className="piece-grid">
            {piecesToDisplay.map((piece) => (
              <div
                key={piece.type}
                className={`piece-card ${
                  selectedPieceType === piece.type ? "selected" : ""
                }`}
                onClick={() => setSelectedPieceType(
                  selectedPieceType === piece.type ? null : piece.type
                )}
                style={{
                  borderColor:
                    selectedPieceType === piece.type
                      ? getClassColor(piece.class)
                      : "#000",
                }}
              >
                <div className="piece-card-image">
                  <img
                    src={getPieceIcon(piece.abbreviation, "white")}
                    alt={piece.name}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <span
                    className="piece-level-badge"
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      backgroundColor: getLevelColor(getPieceLevel(piece.abbreviation)),
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      padding: '2px 4px',
                      borderRadius: '3px',
                    }}
                  >
                    {getPieceLevel(piece.abbreviation)}
                  </span>
                </div>
                <div className="piece-card-info">
                  <div className="piece-name">{piece.name}</div>
                  <div className="piece-abbr">{piece.abbreviation}</div>
                  <div className="piece-badges">
                    <div
                      className="piece-class"
                      style={{ backgroundColor: getClassColor(piece.class) }}
                    >
                      {piece.class}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSuccessModal(false)}
        >
          <div
            className="modal-content success-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header success-header">
              <h2>✅ Variant Saved Successfully!</h2>
            </div>
            <div className="modal-body">
              <p className="success-message">
                <strong>"{variantName}"</strong> has been saved!
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginBottom: "15px",
                }}
              >
                {variantCount} / {MAX_CUSTOM_VARIANTS} variants saved
              </p>
              <ul className="success-list">
                <li>✓ Saved to server database</li>
                <li>✓ Added to Play Online's Game Preset dropdown</li>
                <li>✓ Backup JSON file downloaded</li>
                <li>✓ Synced across your devices</li>
              </ul>
              <p className="success-instruction">
                Go to the <strong>Play Online</strong> tab to play your variant!
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="modal-btn confirm-btn"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div
            className="modal-content error-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header error-header">
              <h2>❌ Error</h2>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: "pre-line" }}>{errorMessage}</p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowErrorModal(false)}
                className="modal-btn confirm-btn"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Board Confirmation Modal */}
      {showClearBoardModal && (
        <div className="modal-overlay" onClick={() => setShowClearBoardModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Clear Board</h2>
            </div>
            <div className="modal-body">
              <p>Clear all pieces from the board?</p>
              <p style={{ color: "#c62828", fontWeight: 600, marginTop: "10px" }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowClearBoardModal(false)}
                className="modal-btn cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmClearBoard}
                className="modal-btn confirm-btn"
                style={{ backgroundColor: "#f44336" }}
              >
                Clear Board
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Placement Rules Modal */}
      {showPlacementRulesModal && (
        <div className="modal-overlay" onClick={() => setShowPlacementRulesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>{boardSize}x{boardSize} Board Placement Rules</h2>
            </div>
            <div className="modal-body">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Piece Color</th>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Board Location</th>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>Level</th>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Permissible Pieces</th>
                  </tr>
                </thead>
                <tbody>
                  {boardSize === 8 ? (
                    <>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a1 / h1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b1 / g1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c1 / f1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a8 / h8</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b8 / g8</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c8 / f8</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d8</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e8</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a2-h2</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>PWN</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a7-h7</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>PWN</td>
                      </tr>
                    </>
                  ) : boardSize === 9 ? (
                    <>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a1 / i1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b1 / h1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c1 / g1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d1 / f1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e1</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a9 / i9</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b9 / h9</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c9 / g9</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d9 / f9</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e9</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a2-i2</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>PWN, KNV</td>
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a8-i8</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>PWN, KNV</td>
                      </tr>
                    </>
                  ) : boardSize === 10 ? (
                    <>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a1 / j1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b1 / i1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c1 / h1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d1 / g1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a10 / j10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b10 / i10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c10 / h10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d10 / g10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a2-j2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNV, SQR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a9-j9</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNV, SQR</td></tr>
                    </>
                  ) : boardSize === 11 ? (
                    <>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a1 / k1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b1 / j1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c1 / i1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d1 / h1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e1 / g1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a2 / b2 / c2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d2 / h2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e2 / g2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>i2 / j2 / k2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a3-k3</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNV, SQR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a11 / k11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b11 / j11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c11 / i11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d11 / h11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e11 / g11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a10 / b10 / c10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d10 / h10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e10 / g10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>i10 / j10 / k10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a9-k9</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNV, SQR</td></tr>
                    </>
                  ) : boardSize === 12 ? (
                    <>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a1 / l1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b1 / k1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c1 / j1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d1 / i1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e1 / h1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>g1</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a2 / b2 / c2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d2 / i2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e2 / f2 / g2 / h2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>j2 / k2 / l2</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>White</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a3-l3</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNV, SQR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a12 / l12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>b12 / k12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>c12 / j12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d12 / i12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e12 / h12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>f12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>C</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>g12</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a11 / b11 / c11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>d11 / i11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I / H</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '10px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD, EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>e11 / f11 / g11 / h11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>H</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>j11 / k11 / l11</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>I</td><td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td></tr>
                      <tr><td style={{ border: '1px solid #ddd', padding: '8px' }}>Black</td><td style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#fffde7' }}>a10-l10</td><td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>L</td><td style={{ border: '1px solid #ddd', padding: '8px' }}>KNV, SQR</td></tr>
                    </>
                  ) : null}
                </tbody>
              </table>
              <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
                <p><strong>Level Key:</strong> L = Low, I = Intermediate, H = High, C = Checkmate</p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowPlacementRulesModal(false)}
                className="modal-btn confirm-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Level Guide Modal */}
      {showInstructionsModal && (
        <div className="modal-overlay" onClick={() => setShowInstructionsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Level Guide</h2>
            </div>
            <div className="modal-body">
              <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#333' }}>Understanding Level Badges</h4>
              <p style={{ marginBottom: '15px', lineHeight: '1.6' }}>
                Each piece in the catalog has a Level Badge showing its strength level.
              </p>
              <p style={{ marginBottom: '15px', lineHeight: '1.6' }}>
                The chess board squares also display level badges indicating which piece levels are allowed on that square at the start of the game.
              </p>
              <p style={{ marginBottom: '20px', lineHeight: '1.6' }}>
                <strong>Match the piece level to the square level for valid placement of chess pieces.</strong>
              </p>

              <h4 style={{ marginBottom: '10px', color: '#333' }}>Piece Level Categories and Board Square Badges</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Badge Level</th>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Level Category</th>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Description</th>
                    <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Pieces in Level</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#607D8B', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>L</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#607D8B', color: 'white', padding: '3px 10px', borderRadius: '4px', fontWeight: 'bold', fontSize: '12px' }}>Low</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Forward moving, limited range</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>PWN, KNV, SQR</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#2196F3', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>I</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#2196F3', color: 'white', padding: '3px 10px', borderRadius: '4px', fontWeight: 'bold', fontSize: '12px' }}>Intermediate</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Limited range in multiple directions</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>DUK, CHL, CDL, CTL, ARM, ABY, ARB, CRD, PRS, NUN, MSL, CPN, SGT, GRD, ARC, KNT, RAM, MAC, LNC, HCT, SWD, SNT, WZD</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#9C27B0', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>H</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#9C27B0', color: 'white', padding: '3px 10px', borderRadius: '4px', fontWeight: 'bold', fontSize: '12px' }}>High</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Unlimited range in multiple directions</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>EPS, QUN, FTS, CSL, POP, BSP, CTP, AGL</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#F44336', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>C</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      <span style={{ backgroundColor: '#F44336', color: 'white', padding: '3px 10px', borderRadius: '4px', fontWeight: 'bold', fontSize: '12px' }}>Checkmate</span>
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Game-ending pieces (required)</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>KNG, EPR</td>
                  </tr>
                </tbody>
              </table>

              <p style={{ marginBottom: '20px', lineHeight: '1.6', fontStyle: 'italic', color: '#666' }}>
                <strong>Reference</strong> - see Chapters 9 and 10 of Expanded Chess Manual in Documents tab for detailed explanation of Level Categories.
              </p>

              <h4 style={{ marginBottom: '10px', color: '#333' }}>Tips</h4>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                <li>The Validation panel will show errors if pieces are placed at incorrect locations. All Validation errors must be eliminated before a variant can be saved and then selected for play.</li>
                <li>Check the "Show Placement Rules" checkbox in the Guidelines panel for detailed position rules.</li>
                <li>Use the level filters to find pieces by level (<span style={{ backgroundColor: '#607D8B', color: 'white', padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold', fontSize: '11px', marginRight: '2px' }}>L</span><span style={{ backgroundColor: '#2196F3', color: 'white', padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold', fontSize: '11px', marginRight: '2px' }}>I</span><span style={{ backgroundColor: '#9C27B0', color: 'white', padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold', fontSize: '11px', marginRight: '2px' }}>H</span><span style={{ backgroundColor: '#F44336', color: 'white', padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold', fontSize: '11px' }}>C</span>).</li>
              </ul>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowInstructionsModal(false)}
                className="modal-btn confirm-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantCreator;
